import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation, Trans } from "react-i18next";
import "./PublicWebsite.css";
import PublicHeader from "./PublicHeader";
import Tabs from "../../components/DataTable/Tabs";
import { appConfig } from "../../utils/config";
import { useSafeLogin } from "../../hooks/auth/useSafeLogin";
import GuestCharts from "./GuestCharts/GuestCharts";

const DATACATALOG_TAB_KEY = "datacatalog";

export default function PublicWebsite() {
  const { signinRedirect, isLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("description");
  const [activeSection, setActiveSection] = useState(null);
  const region =
    appConfig.client === "CLIENT_B"
      ? {
          es: "de Navarra",
          ca: "de Navarra",
          en: "of Navarra",
          eu: "Nafarroako",
          gl: "de Navarra",
        }[i18n.language] || "de Navarra"
      : "";
  const { login: handleLogin, isLoginPending } = useSafeLogin({
    isLoading,
    signinRedirect,
    t,
    brand: appConfig.brandTheme,
  });

  useEffect(() => {
    const stored = localStorage.getItem("appLng");
    if (stored && stored !== i18n.language) i18n.changeLanguage(stored);
  }, [i18n]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("appLng", lng);
  };

  const handleRegister = async () => {
    await handleLogin({ action: "register" });
  };

  const publicTabs = useMemo(
    () => ({
      description: { label: t("publicWebsite.tabs.description") },
      // datacatalog: {
      //   label: t("publicWebsite.keyFigure", {
      //     defaultValue: "Data in figures",
      //   }),
      // },
    }),
    [t],
  );

  const sidebarConfigs = useMemo(
    () => ({
      description: [
        {
          id: "sec-what",
          label: t("publicWebsite.sidebar.description.what.name"),
        },
      ],
    }),
    [t],
  );

  const contentByTab = useMemo(
    () => ({
      description: [
        {
          id: "sec-what",
          title: t("publicWebsite.sidebar.description.what.title", { region }),
        },
      ],
      [DATACATALOG_TAB_KEY]: [],
    }),
    [t, region],
  );

  const sidebar = useMemo(
    () => sidebarConfigs[activeTab] ?? [],
    [sidebarConfigs, activeTab],
  );
  const sections = useMemo(
    () => contentByTab[activeTab] ?? [],
    [contentByTab, activeTab],
  );
  const hasSidebar = sidebar.length > 0;

  const isDatacatalog = activeTab === DATACATALOG_TAB_KEY;

  useEffect(() => {
    if (isDatacatalog) {
      setActiveSection(null);
    } else if (sections.length && !activeSection) {
      setActiveSection(sections[0].id);
    } else if (
      sections.length &&
      !sections.find((s) => s.id === activeSection)
    ) {
      setActiveSection(sections[0].id);
    }
  }, [sections, activeSection, isDatacatalog]);

  const visibleSections = useMemo(() => {
    if (isDatacatalog) return [];
    if (!sections.length) return [];
    if (!activeSection) return [sections[0]];
    return sections.filter((s) => s.id === activeSection);
  }, [sections, activeSection, isDatacatalog]);

  return (
    <div className="page-with-fixed-header">
      <PublicHeader
        logo={appConfig.logoFourth}
        i18n={i18n}
        t={t}
        isLoading={isLoading || isLoginPending}
        onLogin={() => handleLogin()}
        onRegister={handleRegister}
        onChangeLanguage={changeLanguage}
      />
      <div className="tabs-section">
        <div className="tabs-header">
          <h1 className="tabs-title">
            {t(`clients.${appConfig.client}.title`)}
          </h1>
        </div>

        <div className="tabs-bar">
          <Tabs
            tabs={publicTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            classNames={{
              container: "tabs-pw-container",
              button: "tab-pw-btn",
              active: "active",
            }}
          />
        </div>
        <div className={isDatacatalog ? "tab-shell-full" : "tab-shell"}>
          {hasSidebar && (
            <aside className="tab-side">
              <ul className="side-list">
                {sidebar.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setActiveSection(item.id)}
                      className={activeSection === item.id ? "active" : ""}
                      aria-current={
                        activeSection === item.id ? "true" : undefined
                      }
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          )}
          {isDatacatalog ? (
            <div className="datacatalog-wrapper">
              <GuestCharts />
            </div>
          ) : (
            <main className={hasSidebar ? "tab-scroll" : "tab-scroll-full"}>
              <div className="tab-panel">
                {visibleSections.length > 0 ? (
                  visibleSections.map((sec) => {
                    const sectionKey = sec.id.replace("sec-", "");
                    const baseKey = `publicWebsite.sidebar.description.${sectionKey}.text`;

                    return (
                      <section
                        key={sec.id}
                        id={sec.id}
                        className="content-narrow"
                        style={{ marginBottom: 24 }}
                      >
                        <h2>{sec.title}</h2>
                        {[0, 1, 2, 3, 4].map((i) => (
                          <p key={i}>
                            <Trans
                              i18nKey={`${baseKey}.${i}`}
                              values={{ region }}
                              components={{
                                bold: <strong />,
                                spaced: <span className="extra-space" />,
                              }}
                            />
                          </p>
                        ))}
                      </section>
                    );
                  })
                ) : (
                  <div style={{ color: "#64748b" }}>
                    {t(
                      "publicWebsite.selectASection",
                      "Selecciona una sección de la barra lateral.",
                    )}
                  </div>
                )}
              </div>
            </main>
          )}
        </div>
      </div>
    </div>
  );
}
